import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { api } from '../services/api.js';
import { Settings, Save, Building2, Palette, CreditCard, ImageIcon as _ImageIcon } from 'lucide-react';
import LogoUpload from '../components/settings/LogoUpload.js';

interface SettingsForm {
  company_name: string;
  company_address: string;
  company_postal_code: string;
  company_city: string;
  company_country: string;
  tax_number: string;
  vat_id: string;
  company_email: string;
  company_phone: string;
  lieferschein_prefix: string;
  brand_color: string;
  bank_name: string;
  bank_iban: string;
  bank_bic: string;
  lieferschein_footer_text: string;
}

const EMPTY: SettingsForm = {
  company_name: '',
  company_address: '',
  company_postal_code: '',
  company_city: '',
  company_country: 'Germany',
  tax_number: '',
  vat_id: '',
  company_email: '',
  company_phone: '',
  lieferschein_prefix: 'LS',
  brand_color: '#1e40af',
  bank_name: '',
  bank_iban: '',
  bank_bic: '',
  lieferschein_footer_text: '',
};

const TeamSettings: React.FC = () => {
  const [form, setForm] = useState<SettingsForm>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/team-settings');
      const s = res.data;
      setForm({
        company_name: s.company_name || '',
        company_address: s.company_address || '',
        company_postal_code: s.company_postal_code || '',
        company_city: s.company_city || '',
        company_country: s.company_country || 'Germany',
        tax_number: s.tax_number || '',
        vat_id: s.vat_id || '',
        company_email: s.company_email || '',
        company_phone: s.company_phone || '',
        lieferschein_prefix: s.lieferschein_prefix || 'LS',
        brand_color: s.brand_color || '#1e40af',
        bank_name: s.bank_name || '',
        bank_iban: s.bank_iban || '',
        bank_bic: s.bank_bic || '',
        lieferschein_footer_text: s.lieferschein_footer_text || '',
      });
    } catch {
      // first time — no settings yet
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/team-settings', form);
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const set = (key: keyof SettingsForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const inputCls =
    'w-full p-3 bg-gray-50 border-2 border-gray-100 rounded-2xl font-bold focus:border-brand-500 transition-colors text-base';

  const labelCls = 'block text-xs font-black text-gray-400 uppercase tracking-widest mb-2';

  const field = (
    label: string,
    key: keyof SettingsForm,
    opts?: { type?: string; placeholder?: string; span?: number }
  ) => (
    <div className={opts?.span === 2 ? 'md:col-span-2' : ''}>
      <label className={labelCls}>{label}</label>
      <input
        type={opts?.type || 'text'}
        value={form[key]}
        onChange={set(key)}
        placeholder={opts?.placeholder}
        className={inputCls}
      />
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-4 md:p-8 text-center py-20 text-gray-400 font-bold">
        Loading...
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-6">
      <div>
        <h1 className="text-2xl md:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
          <Settings size={28} className="text-brand-500" />
          Team Settings
        </h1>
        <p className="text-gray-500 font-medium font-mono uppercase text-xs tracking-tighter mt-1">
          Company info, branding, banking, delivery note configuration
        </p>
      </div>

      {/* Logo card — outside main form so file upload doesn't interfere with submit */}
      <div className="bg-white rounded-[2rem] border-2 border-gray-100 shadow-sm p-6 md:p-8 space-y-5">
        <div className="flex items-center gap-2 mb-2">
          <Building2 size={18} className="text-brand-500" />
          <h2 className="text-lg font-black text-gray-800">Company Logo</h2>
        </div>
        <LogoUpload />
      </div>

      <form onSubmit={handleSave} className="space-y-8">
        {/* Company Information */}
        <div className="bg-white rounded-[2rem] border-2 border-gray-100 shadow-sm p-6 md:p-8 space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <Building2 size={18} className="text-brand-500" />
            <h2 className="text-lg font-black text-gray-800">Company Information</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('Company Name', 'company_name', { span: 2, placeholder: 'Brand Circuit UG' })}
            {field('Street Address', 'company_address', { span: 2, placeholder: 'Musterstr. 1' })}
            {field('Postal Code', 'company_postal_code', { placeholder: '10115' })}
            {field('City', 'company_city', { placeholder: 'Berlin' })}
            {field('Country', 'company_country', { placeholder: 'Germany' })}
            {field('Email', 'company_email', { type: 'email', placeholder: 'info@brandcircuit.de' })}
            {field('Phone', 'company_phone', { placeholder: '+49 30 12345678' })}
          </div>
        </div>

        {/* Tax & Legal */}
        <div className="bg-white rounded-[2rem] border-2 border-gray-100 shadow-sm p-6 md:p-8 space-y-5">
          <h2 className="text-lg font-black text-gray-800">Tax &amp; Legal</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('Tax Number (Steuernummer)', 'tax_number', { placeholder: '27/001/12345' })}
            {field('VAT ID (USt-IdNr.)', 'vat_id', { placeholder: 'DE123456789' })}
          </div>
        </div>

        {/* Branding */}
        <div className="bg-white rounded-[2rem] border-2 border-gray-100 shadow-sm p-6 md:p-8 space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <Palette size={18} className="text-brand-500" />
            <h2 className="text-lg font-black text-gray-800">Branding</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <label className={labelCls}>Brand Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.brand_color}
                  onChange={set('brand_color')}
                  className="h-12 w-14 rounded-xl border-2 border-gray-100 bg-gray-50 cursor-pointer p-1"
                />
                <input
                  type="text"
                  value={form.brand_color}
                  onChange={set('brand_color')}
                  maxLength={7}
                  placeholder="#1e40af"
                  className={`${inputCls} font-mono`}
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>Preview</label>
              <div
                className="h-12 rounded-2xl border-2 border-gray-100 flex items-center justify-center"
                style={{ backgroundColor: form.brand_color }}
              >
                <span className="text-white font-black text-xs uppercase tracking-widest drop-shadow">
                  Brand Color
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Banking */}
        <div className="bg-white rounded-[2rem] border-2 border-gray-100 shadow-sm p-6 md:p-8 space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard size={18} className="text-brand-500" />
            <h2 className="text-lg font-black text-gray-800">Bank Details</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('Bank Name', 'bank_name', { span: 2, placeholder: 'Sparkasse Berlin' })}
            {field('IBAN', 'bank_iban', { span: 2, placeholder: 'DE89 3704 0044 0532 0130 00' })}
            {field('BIC / SWIFT', 'bank_bic', { placeholder: 'COBADEFFXXX' })}
          </div>
        </div>

        {/* Delivery note settings */}
        <div className="bg-white rounded-[2rem] border-2 border-gray-100 shadow-sm p-6 md:p-8 space-y-5">
          <h2 className="text-lg font-black text-gray-800">Delivery note settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('Delivery note prefix', 'lieferschein_prefix', { placeholder: 'LS' })}
            <div>
              <label className={labelCls}>Number Format Preview</label>
              <div className="p-3 bg-gray-50 border-2 border-gray-100 rounded-2xl font-mono font-bold text-brand-600">
                {form.lieferschein_prefix || 'LS'}-{new Date().getFullYear()}-0001
              </div>
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Footer Text</label>
              <textarea
                value={form.lieferschein_footer_text}
                onChange={set('lieferschein_footer_text')}
                rows={3}
                placeholder="e.g. Thank you for your order. Payment due within 14 days."
                className="w-full p-3 bg-gray-50 border-2 border-gray-100 rounded-2xl font-bold focus:border-brand-500 transition-colors text-base resize-none"
              />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full py-4 bg-brand-600 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-brand-700 transition-all active:scale-95 disabled:opacity-50 text-sm flex items-center justify-center gap-2"
        >
          <Save size={18} />
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
      </form>
    </div>
  );
};

export default TeamSettings;
